import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validates input to prevent HTML injection attacks.
 * This validator flags potentially malicious HTML tags, scripts, and related XSS patterns,
 * but does not modify or sanitize the control value.
 */
export const inputSanitizerValidator =
  (): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Skip validation for null, undefined, or empty values
    if (!value || typeof value !== 'string') {
      return null;
    }

    // Patterns to detect HTML injection attempts
    const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
    const scriptPattern = /<script[\s\S]*?>[\s\S]*?<\/script>/gi;
    const onEventPattern = /\bon\w+\s*=/gi;
    const javascriptPattern = /javascript:/gi;
    const vbscriptPattern = /vbscript:/gi;
    const dataUriPattern = /data:text\/html/gi;

    // Patterns to detect HTML entity-encoded attacks
    const encodedLessThan = /(&lt;|&#60;|&#0*60;|&#x3[cC];|&#x0*3[cC];)/i;
    const encodedGreaterThan = /(&gt;|&#62;|&#0*62;|&#x3[eE];|&#x0*3[eE];)/i;
    const encodedScript = /(&lt;|&#60;|&#x3[cC];)\s*script/gi;
    const encodedTag = /(&lt;|&#60;|&#x3[cC];)\s*\/?\s*[a-z]/i;
    const encodedOnEvent = /(&quot;|&#34;|&#x22;|'|&#39;|&#x27;)\s*on\w+\s*=/gi;

    // Check for malicious patterns
    if (
      htmlTagPattern.test(value) ||
      scriptPattern.test(value) ||
      onEventPattern.test(value) ||
      javascriptPattern.test(value) ||
      vbscriptPattern.test(value) ||
      dataUriPattern.test(value) ||
      (encodedLessThan.test(value) && encodedTag.test(value)) ||
      (encodedGreaterThan.test(value) && encodedLessThan.test(value)) ||
      encodedScript.test(value) ||
      encodedOnEvent.test(value)
    ) {
      return {
        htmlInjection: {
          value,
          message: 'Input contains potentially malicious content',
        },
      };
    }

    return null;
  };

/**
 * Sanitizes a string value by removing HTML tags and potentially malicious content.
 * This function provides a safe, sanitized version of the input.
 *
 * @param value - The string value to sanitize
 * @returns The sanitized string with HTML tags and malicious content removed
 */
export function sanitizeInput(value: string | null | undefined): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  // Remove script tags and their content
  let sanitized = value.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');

  // Remove all other HTML tags
  sanitized = sanitized.replace(/<\/?[^>]+(>|$)/g, '');

  // Remove JavaScript protocol
  sanitized = sanitized.replace(/javascript:/gi, '');

  // Remove VBScript protocol
  sanitized = sanitized.replace(/vbscript:/gi, '');

  // Remove on* event handlers
  sanitized = sanitized.replace(/\bon\w+\s*=/gi, '');

  // Remove data URIs
  sanitized = sanitized.replace(/data:text\/html[^;]*;/gi, '');

  // Remove entity-encoded script tags and their content
  sanitized = sanitized.replace(
    /(&lt;|&#60;|&#0*60;|&#x3[cC];|&#x0*3[cC];)\s*script[\s\S]*?(&lt;|&#60;|&#0*60;|&#x3[cC];|&#x0*3[cC];)\s*\/\s*script\s*(&gt;|&#62;|&#0*62;|&#x3[eE];|&#x0*3[eE];)/gi,
    '',
  );

  // Remove other HTML entity-encoded tag attempts
  sanitized = sanitized.replace(
    /(&lt;|&#60;|&#0*60;|&#x3[cC];|&#x0*3[cC];)[^&]*(&gt;|&#62;|&#0*62;|&#x3[eE];|&#x0*3[eE];)/gi,
    '',
  );

  return sanitized.trim();
}

/**
 * Strict validator that blocks common XSS patterns without auto-sanitization.
 * Use this when you want to require users to fix their input manually.
 */
export const strictHtmlValidator =
  (): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value || typeof value !== 'string') {
      return null;
    }

    // Comprehensive XSS pattern detection
    const xssPatterns = [
      /<script[\s\S]*?>[\s\S]*?<\/script>/gi, // Script tags
      /<iframe[\s\S]*?>/gi, // iframes
      /<object[\s\S]*?>/gi, // Object tags
      /<embed[\s\S]*?>/gi, // Embed tags
      /javascript:/gi, // JavaScript protocol
      /vbscript:/gi, // VBScript protocol
      /data:text\/html/gi, // Data URIs
      /\bon\w+\s*=/gi, // Event handlers (onclick, onerror, etc.)
      /<img[\s\S]*?onerror/gi, // Image with onerror
      /<svg[\s\S]*?onload/gi, // SVG with onload
      /<\/?[a-z][\s\S]*?>/i, // Any HTML tag
      /(&lt;|&#60;|&#0*60;|&#x3[cC];)\s*script/gi, // Entity-encoded script tags
      /(&lt;|&#60;|&#x3[cC];)\s*iframe/gi, // Entity-encoded iframes
      /(&lt;|&#60;|&#x3[cC];)\s*object/gi, // Entity-encoded objects
      /(&lt;|&#60;|&#x3[cC];)\s*embed/gi, // Entity-encoded embeds
      /(&lt;|&#60;|&#x3[cC];)\s*img/gi, // Entity-encoded images
      /(&lt;|&#60;|&#x3[cC];)\s*svg/gi, // Entity-encoded SVG
      /(&quot;|&#34;|&#x22;|'|&#39;|&#x27;)\s*on\w+\s*=/gi, // Entity-encoded event handlers
    ];

    for (const pattern of xssPatterns) {
      if (pattern.test(value)) {
        return {
          strictHtmlViolation: {
            value,
            message:
              'Input contains HTML or script content that is not allowed',
          },
        };
      }
    }

    return null;
  };
